/**
 * Layout Variation Engine
 *
 * Prevents visual monotony by tracking recent layout usage
 * and enforcing rules like "no same layout more than N consecutive times".
 *
 * Stateless externally — caller holds VariationState and passes it in.
 * This makes it pure and testable.
 */
import type { SceneLayoutId, ShowRules } from '../types';
import type { createRng } from './seededRandom';

type Rng = ReturnType<typeof createRng>;

// ─── State ───────────────────────────────────────────────────

export interface VariationState {
  /** Last N layout IDs used (across scenes AND stories) */
  recentLayouts: SceneLayoutId[];
  /** How many times each layout has been used in this show */
  usageCounts: Partial<Record<SceneLayoutId, number>>;
  /** How many consecutive times the current layout was used */
  consecutiveCount: number;
  /** The layout that was most recently used */
  lastLayout: SceneLayoutId | null;
}

export function createVariationState(): VariationState {
  return {
    recentLayouts: [],
    usageCounts: {},
    consecutiveCount: 0,
    lastLayout: null,
  };
}

// ─── Core: pick layout from candidates ───────────────────────

/**
 * Given a list of candidate layouts, pick the best one that:
 * 1. Does not exceed maxConsecutiveSameLayout
 * 2. Has not been overused recently
 * 3. Uses seeded RNG for deterministic tie-breaking
 *
 * Returns the chosen layout and the updated state.
 */
export function pickLayout(
  candidates: SceneLayoutId[],
  state: VariationState,
  rules: ShowRules,
  rng: Rng,
): { layout: SceneLayoutId; nextState: VariationState } {
  if (candidates.length === 0) {
    throw new Error('pickLayout: candidates array is empty');
  }

  const { maxConsecutiveSameLayout } = rules;
  const RECENT_WINDOW = 6; // look back N layouts

  // Score each candidate — lower = more preferred
  const scored = candidates.map((layout) => {
    let penalty = 0;

    // Heavy penalty if we'd exceed consecutive limit
    if (layout === state.lastLayout) {
      penalty += (state.consecutiveCount >= maxConsecutiveSameLayout) ? 1000 : 10;
    }

    // Penalty for recent usage
    const recentUses = state.recentLayouts.slice(-RECENT_WINDOW).filter((l) => l === layout).length;
    penalty += recentUses * 15;

    // Small penalty for overall high usage (prefer variety)
    const totalUses = state.usageCounts[layout] ?? 0;
    penalty += totalUses * 2;

    // Add small random noise for tie-breaking (seeded so deterministic)
    penalty += rng.next() * 5;

    return { layout, penalty };
  });

  // Sort by penalty ascending — lowest penalty wins
  scored.sort((a, b) => a.penalty - b.penalty);
  const chosen = scored[0].layout;

  // Update state
  const nextState: VariationState = {
    recentLayouts: [...state.recentLayouts.slice(-(RECENT_WINDOW - 1)), chosen],
    usageCounts: {
      ...state.usageCounts,
      [chosen]: (state.usageCounts[chosen] ?? 0) + 1,
    },
    consecutiveCount: chosen === state.lastLayout ? state.consecutiveCount + 1 : 1,
    lastLayout: chosen,
  };

  return { layout: chosen, nextState };
}

// ─── Batch: pick a sequence of N layouts ─────────────────────

/**
 * Pick a sequence of N layout IDs for a story.
 * Some slots are forced (e.g. bigNumber → L7), others use variation logic.
 */
export interface LayoutSlot {
  forced?: SceneLayoutId;    // if set, use this exact layout
  candidates?: SceneLayoutId[];
}

export function pickLayoutSequence(
  slots: LayoutSlot[],
  state: VariationState,
  rules: ShowRules,
  rng: Rng,
): { layouts: SceneLayoutId[]; nextState: VariationState } {
  let currentState = state;
  const layouts: SceneLayoutId[] = [];

  for (const slot of slots) {
    if (slot.forced) {
      // Forced layout — still update state
      const nextState: VariationState = {
        recentLayouts: [...currentState.recentLayouts.slice(-5), slot.forced],
        usageCounts: {
          ...currentState.usageCounts,
          [slot.forced]: (currentState.usageCounts[slot.forced] ?? 0) + 1,
        },
        consecutiveCount:
          slot.forced === currentState.lastLayout ? currentState.consecutiveCount + 1 : 1,
        lastLayout: slot.forced,
      };
      layouts.push(slot.forced);
      currentState = nextState;
    } else {
      const candidates = slot.candidates ?? [];
      if (candidates.length === 0) continue;
      const { layout, nextState } = pickLayout(candidates, currentState, rules, rng);
      layouts.push(layout);
      currentState = nextState;
    }
  }

  return { layouts, nextState: currentState };
}
